import { useState, useEffect, useRef } from "react"

import Modal from "shared/components/modal"
import SuccessModal from "app/components/billing/success_modal"
import { changeBillingInterval } from "app/components/upgrade_plan_modal/pro_upgrade/api"

import iftttLogo from "images/logo-ifttt.svg?raw"
import { planNameBadge } from "shared/utils/tiers"

import styles from "app/components/upgrade_plan_modal/shared_modal.module.scss"

interface Props {
  annualPrice: string
  annualPriceId: string
  discountPercentage: number
  planName: string
}

const BillingUpgradeAnnualModal = ({ annualPrice, annualPriceId, discountPercentage, planName }: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const containerRef = useRef<HTMLSpanElement>(null)

  const openModal = () => {
    setShowModal(true)
  }

  const handleLinkClick = (evt: MouseEvent) => {
    evt.preventDefault()
    openModal()
  }

  // Make the parent .plan-interval-banner clickable
  useEffect(() => {
    const banner = containerRef.current?.closest(".plan-interval-banner") as HTMLElement | null
    if (!banner) return

    const handleBannerClick = (evt: Event) => {
      const target = evt.target as HTMLElement
      if (target.tagName === "A") return
      openModal()
    }

    banner.addEventListener("click", handleBannerClick)
    return () => banner.removeEventListener("click", handleBannerClick)
  }, [])

  const handleClose = () => {
    setShowModal(false)
  }

  const handleUpgrade = async () => {
    setLoading(true)

    try {
      const { errors } = await changeBillingInterval({
        targetInterval: "year",
        priceId: annualPriceId,
      })

      if (!errors?.length) {
        setShowModal(false)
        setShowSuccess(true)
      } else {
        const errorMessage = errors[0]?.message || "Something went wrong. Please try again."
        window.appendFlash(errorMessage, "danger")
        setShowModal(false)
      }
    } catch {
      window.appendFlash("Something went wrong. Please try again.", "danger")
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  if (showSuccess) {
    return (
      <SuccessModal
        title="Successfully updated to annual"
        badge={iftttLogo + planNameBadge(planName)}
        successContent={
          <>
            <p>
              We charged {annualPrice}, minus the remaining balance from your current month, to your saved payment
              method you have on file.
            </p>
            <p>Enjoy your extra savings!</p>
          </>
        }
        visible={true}
        onClose={() => window.location.reload()}
        buttons={[
          {
            label: "Okay",
            onClick: () => window.location.reload(),
            variant: "secondary",
          },
        ]}
      />
    )
  }

  return (
    <span ref={containerRef}>
      <a onClick={handleLinkClick} className="link-standalone">
        Switch to annual
      </a>
      {showModal && (
        <div className={styles["modal-container"]} data-fullscreen-modal>
          <Modal show={showModal} closeListener={handleClose} includeCloseX={true}>
            <h2 className="mt0 mb0">Save {discountPercentage}% with annual plans</h2>
            <p>
              By switching to annual, you save money each year on the same plan! Enjoy the same benefits at a lower
              cost.
            </p>
            <p>
              You will be billed {annualPrice} annually to the payment method you have on file. The remaining balance
              from your current month will be applied to your new subscription.
            </p>
            <div className="action-buttons-row">
              <button className="button-tertiary button-outlined" onClick={handleClose} disabled={loading}>
                Nevermind
              </button>
              <button className="button-tertiary" disabled={loading} onClick={handleUpgrade}>
                {loading ? "Updating..." : `Update to ${planName} annual plan`}
              </button>
            </div>
          </Modal>
        </div>
      )}
    </span>
  )
}

export default BillingUpgradeAnnualModal
